import { useEffect, useState } from 'react'
import { isEnglish } from '../lib/i18n'
import './EnvironmentBadge.css'

const ENVIRONMENTS = {
  DEV: { en: 'DEV', ar: 'تطوير (DEV)' },
  DEVELOPMENT: { en: 'DEV', ar: 'تطوير (DEV)' },
  QA: { en: 'QA', ar: 'اختبار (QA)' },
  TEST: { en: 'QA', ar: 'اختبار (QA)' },
  TESTING: { en: 'QA', ar: 'اختبار (QA)' },
  PROD: null,
  PRODUCTION: null,
}

/** `{ full, short }` — the short one is what a narrow header has room for. */
function formatEnvironmentLabel(label) {
  const value = String(label ?? '').trim().slice(0, 24)
  if (!value) return null

  const environment = ENVIRONMENTS[value.toUpperCase()]
  if (environment === null) return null
  if (environment) return { full: isEnglish ? environment.en : environment.ar, short: environment.en }

  return { full: value, short: value }
}

/** One request per page load, however many badges ask — the label cannot change under it. */
let pending = null
const loadLabel = () => {
  pending ??= fetch('/api/environment', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((payload) => formatEnvironmentLabel(payload?.label))
    .catch(() => null)
  return pending
}

/**
 * Runtime deployment marker. The same image can serve development and production:
 * the server returns an empty label in production, so no marker is rendered there.
 *
 * TWO PLACEMENTS. `inline` sits in a shell's header — the admin topbar, the learner's
 * header — where it takes its own room. `floating` is the fallback for a page with no
 * header (sign-in, install), and it stands down while an inline one is mounted: pinned
 * over the corner of the viewport it covered whatever ended there, from the footer's
 * version to a stepper's `التالي` (Taiga #1515, #1532).
 */
export default function EnvironmentBadge({ placement = 'floating' }) {
  const [label, setLabel] = useState(null)
  const inline = placement === 'inline'

  useEffect(() => {
    let live = true
    loadLabel().then((value) => live && setLabel(value))
    return () => {
      live = false
    }
  }, [])

  /* While a badge is up, the root says which kind — see EnvironmentBadge.css. */
  useEffect(() => {
    if (!label) return undefined
    const root = document.documentElement
    const flag = inline ? 'has-inline-environment-badge' : 'has-environment-badge'
    root.classList.add(flag)
    return () => root.classList.remove(flag)
  }, [label, inline])

  if (!label) return null

  return (
    <div
      className={`environment-badge environment-badge--${inline ? 'inline' : 'floating'}`}
      role="status"
      aria-label={`${label.short} environment`}
    >
      <span className="environment-badge__full">{label.full}</span>
      <span className="environment-badge__short" aria-hidden="true">
        {label.short}
      </span>
    </div>
  )
}
